import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import assets from '@/store/assets';
import employeeStore from '@/store/modules/employee';
import reviewStore from '@/store/modules/review';
import reportStore from '@/store/modules/report';
import settingsStore from '@/store/modules/settings';
import goalsStore from '@/store/modules/goals';
import successionPlanStore from '@/store/modules/talent-management/succession-plan';
import idpStore from '@/store/modules/talent-management/individual-development-plan';
import { buildParams } from '@/helpers/url';
import competencyGroup from '@/store/modules/talent-management/competency-group';

Vue.use(Vuex);
export default new Vuex.Store({
  state: {
    breadcrumbs: [],
    title: '',
    tokenData: '',
    superAdminData: '',
    consultantData: {},
    companiesData: '',
    userData: {},
    realCompanyId: '',
    isQuotaBased: false,
    isActionRestricted: false,
    quotaData: {
      active_user: 0,
      limit: 0,
      grace_period_remaining_days: 0,
    },
    roleData: [
      { id: 1, name: 'Super Admin' },
      { id: 3, name: 'Admin' },
      { id: 4, name: 'Consultant' },
      { id: 5, name: 'Finance' },
      { id: 6, name: 'Consultant Admin' },
    ],
    organizationData: [],
    branchData: [],
    jobLevelData: [],
    jobPositionData: [],
    employeeStatusData: [],
    // Filter options by company ID (for calibration and other features)
    filterByCompanyId: {
      organizations: [],
      branches: [],
      jobPositions: [],
      jobLevels: [],
    },
    sidebarNotif: {
      pending_action: 0,
    },
    maxFileSize: 10485760,
    allowedFileTypes: '.pdf, .jpg, .png, .xlsx, .xls, .jpeg, .docx, .doc, .csv, .txt, .ppt, .pptx',
    canAccessSuccessionPlan: false,
    isKakiTangan: false,
    isHideIntercom: true,
  },
  getters: {
    tokenData(state) {
      return state.tokenData;
    },
    superAdminData(state) {
      return state.superAdminData;
    },
    consultantData(state) {
      return state.consultantData;
    },
    companiesData(state) {
      if (state.companiesData) return state.companiesData;

      const { companies } = localStorage;
      return companies && typeof companies === 'string' ? JSON.parse(companies) : [];
    },
    userData(state) {
      return state.userData;
    },
    realCompanyId(state) {
      return state.realCompanyId;
    },
    roleData(state) {
      return state.roleData;
    },
    organizationData(state) {
      return state.organizationData;
    },
    branchData(state) {
      return state.branchData;
    },
    employeeStatusesData(state) {
      return state.employeeStatusesData;
    },
    availableFiltersOptions(state) {
      // Branch role { id: '...', name: '...' }
      const branchData = state.branchData.map(x => {
        return { id: `branch-${x.id}`, content: x.name, children: [] };
      });

      // Organization role { id: '...', name: '...' }
      const organizationData = state.organizationData.map(x => {
        return { id: `organization-${x.id}`, content: x.name, children: [] };
      });

      // Job Position role { "id": 8708, "job": "CEO" }
      const jobPositionData = state.jobPositionData.map(x => {
        return { id: `job-${x.id}`, content: x.name, children: [] };
      });

      // Job Level role { "id": 2036, "title": "CEO",  }
      const jobLevelData = state.jobLevelData.map(x => {
        return { id: `jobLevel-${x.id}`, content: x.name, children: [] };
      });

      return {
        branchData,
        organizationData,
        jobPositionData,
        jobLevelData,
      };
    },
    jobLevelData(state) {
      return state.jobLevelData;
    },
    jobPositionData(state) {
      return state.jobPositionData;
    },
    employeeStatusData(state) {
      return state.employeeStatusData;
    },
    filterByCompanyId(state) {
      return state.filterByCompanyId;
    },
    getSidebarNotif: state => key => {
      if (state.sidebarNotif[key] > 9) return '9+';

      return state.sidebarNotif[key];
    },
    getGrantAccess(state) {
      return {
        isQuotaBased: state.isQuotaBased || false,
        isQuotaRestricted: state.isActionRestricted || false,
        quotaData: state.quotaData ?? { active_user: 0, limit: 0, grace_period_remaining_days: 0 },
      };
    },
  },
  mutations: {
    storeTokenData(state, tokenData) {
      state.tokenData = tokenData;
    },
    storeSuperAdminData(state, saData) {
      state.superAdminData = saData;
    },
    storeConsultantData(state, consultantData) {
      state.consultantData = consultantData;
    },
    storeCompaniesData(state, companiesData) {
      state.companiesData = companiesData;
      localStorage.companies = JSON.stringify(companiesData);
    },
    SET_QUOTA_BASED(state, { is_quota_based, is_action_restricted, quota }) {
      state.isQuotaBased = is_quota_based; // eslint-disable-line camelcase
      state.isActionRestricted = is_action_restricted; // eslint-disable-line camelcase
      state.quotaData = quota;
    },
    SET_USER_DATA(state, user) {
      state.userData = user;
    },
    SET_REAL_COMPANY_ID(state, id) {
      state.realCompanyId = id;
    },
    storeOrganizationData(state, organizations) {
      state.organizationData = organizations;
    },
    storeBranchData(state, branches) {
      state.branchData = branches;
    },
    storeJobLevelData(state, jobLevels) {
      state.jobLevelData = jobLevels;
    },
    storeJobPositionData(state, jobPositions) {
      state.jobPositionData = jobPositions;
    },
    storeEmployeeStatusData(state, employeeStatus) {
      state.employeeStatusData = employeeStatus;
    },
    storeFilterByCompanyId(state, { organizations, branches, jobPositions, jobLevels }) {
      state.filterByCompanyId = {
        organizations: organizations || [],
        branches: branches || [],
        jobPositions: jobPositions || [],
        jobLevels: jobLevels || [],
      };
    },
    SET_SIDEBAR_NOTIF(state, newData) {
      state.sidebarNotif = { ...state.sidebarNotif, ...newData };
    },
    setCanAccessSuccessionPlan(state, value) {
      state.canAccessSuccessionPlan = value;
      this._vm.$cookies.set('can-access-succession-plan', value);
    },
    setIsKakiTangan(state, value) {
      state.isKakiTangan = value;
    },
    setIsHideIntercom(state, value) {
      state.isHideIntercom = value;
    },
  },
  actions: {
    storeTokenData({ commit }, tokenData) {
      commit('storeTokenData', tokenData);
      commit('SET_USER_DATA', tokenData.user);
      commit('SET_REAL_COMPANY_ID', tokenData.user.company_id);
    },
    storeSuperAdminData({ commit }, superAdminData) {
      commit('storeSuperAdminData', superAdminData);
    },
    storeConsultantData({ commit }, consultantData) {
      commit('storeConsultantData', consultantData);
    },
    storeCompaniesData({ commit }, companiesData) {
      commit('storeCompaniesData', companiesData);
    },
    async getUserFilters({ commit, state }, { showAll, hideIntialBranch }) {
      try {
        if (
          state.organizationData.length &&
          state.branchData.length &&
          state.jobLevelData.length &&
          state.jobPositionData.length &&
          state.employeeStatusData.length
        ) {
          return;
        }

        const param = {
          ...(showAll && { show_all: true }),
          ...(hideIntialBranch && { show_initial_branch: false }),
        };
        const url = `${this._vm.$endPoint}/helpers/user-filter?${buildParams(param)}`;
        const res = await axios.get(url);

        if (res.status === 200) {
          const organizations = res.data.contents.organizations.map(org => ({
            id: org.id,
            name: org.organization,
          }));

          const branches = res.data.contents.branches.map(branch => ({
            id: branch.id,
            name: branch.name,
          }));

          const jobLevels = res.data.contents.job_levels.map(jobLevel => ({
            id: jobLevel.id,
            name: jobLevel.title,
          }));

          const jobPositions = res.data.contents.job_positions.map(jobPosition => ({
            id: jobPosition.id,
            name: jobPosition.job,
          }));

          const employeeStatuses = await res.data.contents.employee_statuses?.map(status => ({
            id: status.id,
            name: status.custom_employee_status_name,
            is_end_date: status.is_end_date,
          }));

          commit('storeOrganizationData', organizations);
          commit('storeBranchData', branches);
          commit('storeJobLevelData', jobLevels);
          commit('storeJobPositionData', jobPositions);
          commit('storeEmployeeStatusData', employeeStatuses);
        }
      } catch (e) {
        console.log(e);
      }
    },
    async getFilterOptionsByCompanyId({ commit }, companyId) {
      try {
        const [organizationsRes, branchesRes, jobPositionsRes, jobLevelsRes] = await Promise.all([
          axios.get(`${this._vm.$newEndPoint}/organizations/${companyId}`),
          axios.get(`${this._vm.$newEndPoint}/branches/${companyId}`),
          axios.get(`${this._vm.$newEndPoint}/job-positions?company_id=${companyId}`),
          axios.get(`${this._vm.$newEndPoint}/job-levels?company_id=${companyId}`),
        ]);

        const organizations = (organizationsRes.data?.contents || []).map(org => ({
          id: org.id,
          name: org.organization,
        }));
        const branches = (branchesRes.data?.contents || []).map(branch => ({
          id: branch.id,
          name: branch.branch,
        }));
        const jobPositions = (jobPositionsRes.data?.contents || []).map(jobPosition => ({
          id: jobPosition.id,
          name: jobPosition.job_position,
        }));
        const jobLevels = (jobLevelsRes.data?.contents || []).map(jobLevel => ({
          id: jobLevel.id,
          name: jobLevel.job_level || jobLevel.title,
        }));

        commit('storeFilterByCompanyId', {
          organizations,
          branches,
          jobPositions,
          jobLevels,
        });

        return {
          organizations,
          branches,
          jobPositions,
          jobLevels,
        };
      } catch (e) {
        console.error('Error fetching filter options by company ID:', e);
        throw e;
      }
    },
    async checkSuccessionPlanPermission({ commit }, companyId) {
      try {
        const canAccessSuccessionPlanCookie = this._vm.$cookies.isKey('can-access-succession-plan');
        if (canAccessSuccessionPlanCookie) {
          commit('setCanAccessSuccessionPlan', this._vm.$cookies.get('can-access-succession-plan') === 'true');
        } else {
          if (companyId === 3160) {
            // set default true for SDET/automation company
            commit('setCanAccessSuccessionPlan', true);
            return;
          }
          commit('setCanAccessSuccessionPlan', false);
          const url = `${this._vm.$endPoint}/succession-plans/show-succession-plan`;
          const { data } = await this._vm.$axios.get(url);
          commit('setCanAccessSuccessionPlan', data.data.show_succession_plan);
        }
      } catch (e) {
        console.log(e);
      }
    },
    async checkCalibrationPermission() {
      try {
        const { data } = await this._vm.$axios.get(`${this._vm.$newEndPoint}/calibration/permission`);
        this._vm.$cookies.set('can-access-calibration', data.contents.is_exist_data);
      } catch (e) {
        this._vm.$cookies.set('can-access-calibration', false);
      }
    },
    async getUnleash(context, payload) {
      try {
        const { data, response } = await this._vm.$axios.post(`${this._vm.$newNonCoreEndPoint}/toggles/unleash`, {
          toggles: [payload],
          companyId: context.state.consultantData?.company_id || context.state.userData?.company_id,
        });

        if (typeof data === 'boolean') return data || false;

        throw Error(response?.data?.message || data?.message || 'Failed to fetch unleash toggles');
      } catch (err) {
        throw Error(err);
      }
    },
    async trackMixpanel(context, payload) {
      try {
        await this._vm.$axios.post(`${this._vm.$newEndPoint}/mixpanel/general`, payload);
      } catch (e) {
        console.log(e);
      }
    },
    async getLoginToken(context, payload) {
      const url = `${this._vm.$endPoint}/oauth/access_token`;

      try {
        const { data, response } = await this._vm.$axios.post(url, payload);

        if (data?.status === 200 || data?.status_code === 200) {
          const token = data?.contents?.token;
          this._vm.$setSDK(token);
          this._vm.$cookies.set('token', token);
          context.dispatch('checkCalibrationPermission');

          const query = localStorage.query ? JSON.parse(localStorage.query) : '';
          return { query };
        }
        return { error: data?.message || response?.data?.message };
      } catch (err) {
        throw Error(err?.data?.message || err?.response?.data?.message);
      }
    },
    resetCookies({ commit }) {
      this._vm.$cookies.remove('token');
      this._vm.$cookies.remove('consultant');
      this._vm.$cookies.remove('isa-consultant');
      this._vm.$cookies.remove('can-access-report');
      this._vm.$cookies.remove('can-access-succession-plan');
      this._vm.$cookies.remove('can-access-calibration');
      this._vm.$cookies.remove('total-pending-task');
      this._vm.$cookies.remove('encryptedProfile');
      commit('SET_SIDEBAR_NOTIF', { pending_action: '' });
    },
  },
  modules: {
    assets,
    employee: employeeStore,
    review: reviewStore,
    report: reportStore,
    settings: settingsStore,
    goals: goalsStore,
    successionPlan: successionPlanStore,
    idp: idpStore,
    competencyGroup,
  },
});
